/** Game state shape. Bump `version` on any breaking change and add a migration in save.ts. */

export type BoostKind =
  | 'rush'
  | 'frenzy'
  | 'quick-hands'
  | 'golden-touch'
  | 'thunderclap'
  /** A boss fight won: the Victory Roar. */
  | 'roar';
export const BOOST_KINDS: readonly BoostKind[] = [
  'rush',
  'frenzy',
  'quick-hands',
  'golden-touch',
  'thunderclap',
  'roar',
];

/** A timed production multiplier from a caught falling egg. */
export interface Boost {
  kind: BoostKind;
  mult: number;
  /** Unix ms. Real time, so closing the tab does not pause it. */
  endsAt: number;
}

/** What an era left behind, kept for the Era Log and the achievements. */
export interface EraRecord {
  /** 1-based, as the player counts them. */
  era: number;
  /** The modifier it was played under, or null for a plain era. */
  eraId: string | null;
  /** The expedition it was run as, if any. */
  expedition: string | null;
  /** Seconds of wall-clock time it lasted. */
  seconds: number;
  /** Eggs it laid. */
  eggs: number;
  fossils: number;
  /** The dinosaur whose fossil was excavated, if one was. */
  excavated: string | null;
  /**
   * What it was called and which sprite it wore, captured here rather than
   * looked up later: the name book holds only a tier's *latest* name, so a
   * rename would quietly relabel every older plaque, and an evolution would
   * make a Gigantoraptor show as the Oviraptor it used to be. Null on records
   * written before this was kept — the Museum falls back to the book.
   */
  excavatedName: string | null;
  excavatedSprite: string | null;
  /** Species hatched by the end of it. */
  tiers: number;
}

/**
 * Every era is kept. The log was capped at fifty, but the Collection, the
 * History and the retired names are all read from it, so past fifty eras a
 * player lost their oldest exhibits and plaques and those names came free
 * again. A record is about 200 bytes saved; a thousand eras is 200 KB.
 */

export interface GameState {
  version: 11;
  eggs: number;
  totalEggs: number;
  clicks: number;
  /**
   * Level per dinosaur id; absent or 0 means not hatched yet. Each tier is a
   * single dinosaur that grows: level N lays N times its base rate.
   */
  dinos: Record<string, number>;
  /** Ids of purchased upgrades. */
  upgrades: string[];
  /** Ids of earned achievements. */
  achievements: string[];
  /** Unix ms of the last save; used for offline progress. */
  lastSaved: number;
  /** Active falling-egg production boost, if any. */
  boost: Boost | null;
  /** Active falling-egg click boost, if any; runs alongside a production boost. */
  clickBoost: Boost | null;
  /** Falling eggs caught, lifetime. */
  cracks: number;
  /** Pet names the player gave dinosaurs, by id; absent means unnamed. */
  names: Record<string, string>;
  /** Meteor shards in hand (the shard shop's currency). */
  shards: number;
  /** Shards earned, lifetime. */
  shardsEarned: number;
  /** Meteors broken, lifetime. */
  meteorsBroken: number;
  /** Meteors broken per kind id, lifetime. */
  meteorKindsBroken: Record<string, number>;
  /** Ids of shard-shop items bought; a repeatable tile appears once per purchase. */
  shop: string[];
  /** Unused items, by item id, with how many are held. */
  inventory: Record<string, number>;
  /** Ids of dinosaur tiers that have been evolved. */
  evolved: string[];

  // Fossil Eras (docs/fossil-eras-plan.md). A run is one "era", ended by an
  // extinction that buries everything but the fossils it leaves behind.
  /** Fossils in hand. Each one held adds to production; spending them stops that. */
  fossils: number;
  /** Fossils earned, lifetime. */
  fossilsEarned: number;
  /** Eras completed; 0 during the first one. */
  era: number;
  /**
   * Eggs laid in this era. The extinction threshold reads it, and it is the
   * one egg counter that resets — `totalEggs` is lifetime and carries over.
   */
  eggsThisEra: number;
  /** Ids of Museum perks bought (R3; kept here so an extinction preserves them). */
  museum: string[];
  /** The era modifier in force, or null for a plain era (the first one). */
  eraId: string | null;
  /** Unix ms the current era began; the first one starts when the save does. */
  eraStartedAt: number;
  /** The expedition this era is being run as, if any. */
  expedition: string | null;
  /**
   * The era modifiers offered for the next era, drawn the first time the
   * extinction confirm opens and kept until the era ends. Empty until then.
   * Drawn fresh on every opening, closing the dialog was a free reroll.
   */
  eraOffer: string[];
  /** Past eras, newest last, every one of them. */
  eraLog: EraRecord[];
  /**
   * Names given before 2026-09-26, by dinosaur id. Names used to come back
   * when a tier hatched in a later era; now they go with the herd, and this
   * is kept only as the fallback for Museum plaques written before the era
   * log recorded the excavated dinosaur's name. Nothing adds to it.
   */
  nameBook: Record<string, string>;
  /** Whether a share link has ever been used (the Share panel; Word of Mouth). */
  shared: boolean;
  /** Golden eggs caught per bonus id, lifetime (Thunderstruck, Motherlode). */
  bonusesCaught: Record<string, number>;
  /** One-off moments the view records for achievements (game/conditions.ts Feat). */
  feats: string[];
  /**
   * Species hatched, named and evolved in earlier eras, by tier id, for the
   * "every dinosaur, ever" achievements: a resident exists in one era only,
   * so "every dinosaur" cannot mean at once. The current era is not in them;
   * an extinction folds it in (game/rebirth.ts), and the achievements read
   * them together with the herd as it stands (ever() in game/conditions.ts).
   */
  everHatched: string[];
  everNamed: string[];
  everEvolved: string[];
  /** Boss fights: when each dinosaur's rest ends (unix ms), this era. */
  bossRest: Record<string, number>;
  /** Boss fights: the bosses each dinosaur has beaten this era (its titles). */
  bossWins: Record<string, string[]>;
  /** Boss fights: every boss ever beaten, by any dinosaur. Kept for good. */
  bossTrophies: string[];
}

/** Records a one-off moment for the achievements; recording it twice is harmless. */
export function addFeat(state: GameState, feat: string): void {
  if (!state.feats.includes(feat)) state.feats.push(feat);
}

/** Longest pet name; the card's input enforces it and the loader trims to it. */
export const NAME_MAX_LENGTH = 20;

export function createInitialState(now: number = Date.now()): GameState {
  return {
    version: 11,
    eggs: 0,
    totalEggs: 0,
    clicks: 0,
    dinos: {},
    upgrades: [],
    achievements: [],
    lastSaved: now,
    boost: null,
    clickBoost: null,
    cracks: 0,
    names: {},
    shards: 0,
    shardsEarned: 0,
    meteorsBroken: 0,
    meteorKindsBroken: {},
    shop: [],
    inventory: {},
    evolved: [],
    fossils: 0,
    fossilsEarned: 0,
    era: 0,
    eggsThisEra: 0,
    museum: [],
    eraId: null,
    eraStartedAt: now,
    expedition: null,
    eraOffer: [],
    eraLog: [],
    nameBook: {},
    shared: false,
    bonusesCaught: {},
    feats: [],
    everHatched: [],
    everNamed: [],
    everEvolved: [],
    bossRest: {},
    bossWins: {},
    bossTrophies: [],
  };
}

/**
 * Credits eggs everywhere they have to land: the bank, the lifetime total,
 * and the era's own total. Every earning path goes through here so none can
 * forget the era counter the extinction threshold reads.
 */
export function earnEggs(state: GameState, amount: number): void {
  state.eggs += amount;
  state.totalEggs += amount;
  state.eggsThisEra += amount;
}
